// SPDX-License-Identifier: LicenseRef-Pachamama-Proprietary
// Copyright 2026 Pachamama Studios B.V. All rights reserved.

/**
 * AYNI — the legend of a classified release: one swatch and label per
 * class, in the colour the globe gives it.
 *
 * A colour bar cannot label classes (its ticks are numbers on a ramp),
 * and a published legend picture is only there when the publisher made
 * one, so this is drawn from the release itself: the labels from its
 * `classes`, the colours from the same LUT the shader receives
 * (`buildDisplayLut`), so the legend and the globe cannot disagree.
 * Used for the floating legend and in Tools → View.
 */

import { t } from '../i18n'
import { buildDisplayLut, DEFAULT_DISPLAY } from '../services/colorScaleDisplay'
import { releaseOf, type ColorScale } from '../types/color-scale'

export interface ClassLegendOptions {
  scale: ColorScale
  /** Dataset title, for the accessible name. */
  title?: string
}

/** Whether `scale` is a classified release with classes to list. */
export function hasClasses(scale: ColorScale): boolean {
  const release = releaseOf(scale)
  return release?.kind === 'luma8-classified' && release.classes.length > 0
}

/**
 * Build the class legend, or `null` when `scale` has no classes to list.
 * Returns a fresh element each call, as `renderColorbar` does.
 */
export function renderClassLegend(options: ClassLegendOptions): HTMLElement | null {
  const { scale, title } = options
  const release = releaseOf(scale)
  if (!release || !hasClasses(scale)) return null
  // Classes keep their published colours under any display (see
  // `buildReleaseDisplayLut`), so the default display is the true one.
  const lut = buildDisplayLut(scale, DEFAULT_DISPLAY)

  const el = document.createElement('div')
  el.className = 'panel-colorbar panel-class-legend'
  el.setAttribute('role', 'group')
  el.setAttribute('aria-label', title
    ? t('colorbar.classes.ariaWithTitle', { title, count: release.classes.length })
    : t('colorbar.classes.aria', { count: release.classes.length }))

  const list = document.createElement('ul')
  list.className = 'panel-class-legend-list'
  for (const c of release.classes) {
    const o = Math.max(0, Math.min(255, Math.round(c.code))) * 4
    const item = document.createElement('li')
    item.className = 'panel-class-legend-item'
    const swatch = document.createElement('span')
    swatch.className = 'panel-class-legend-swatch'
    swatch.setAttribute('aria-hidden', 'true')
    swatch.style.backgroundColor = `rgb(${lut[o]}, ${lut[o + 1]}, ${lut[o + 2]})`
    const label = document.createElement('span')
    label.className = 'panel-class-legend-label'
    label.textContent = c.label || String(c.value)
    item.append(swatch, label)
    list.appendChild(item)
  }
  // AYNI (owner, 2026-10-11): the list in the bar's column, the units beside it, as on the colour bar.
  const column = document.createElement('div')
  column.className = 'panel-colorbar-scale'
  column.appendChild(list)
  el.appendChild(column)

  if (release.units) {
    const units = document.createElement('div')
    units.className = 'panel-colorbar-units'
    units.textContent = release.units
    units.title = release.units
    el.appendChild(units)
  }
  return el
}
